"use client";
import { isRecitationMeeting } from '@/app/lib/meetingTypes';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { XMarkIcon, CheckIcon, FunnelIcon, AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import { ScheduledCourse, GroupedCourse } from '@/app/types/course';
import teachersData from '@/data/teachers_data.json';
import { openSectionsService } from '@/app/services/api';
import { CAMPUS_COLORS } from '@/app/types/colors';
import Modal from '@/app/Components/Modal';

interface Professor {
  id: string;
  firstName: string;
  lastName: string;
  avgDifficulty: number;
  avgRating: number;
  department: string;
  numRatings: number;
  wouldTakeAgainPercent: number;
}

interface CourseConfigurationModalProps {
  isOpen: boolean;
  onClose: () => void;
  course: GroupedCourse | null;
  onSave: (allowedSections: string[]) => void;
  focusSectionId?: string;
  returnFocusRef?: React.RefObject<HTMLElement | null>;
  /** Matches schedule term for openSectionsService (e.g. spring-2026). */
  openSectionsTermId?: string;
}

const typedTeachersData: Professor[] = teachersData as Professor[];

export default function CourseConfigurationModal({ 
  isOpen, 
  onClose, 
  course, 
  onSave,
  focusSectionId,
  returnFocusRef,
  openSectionsTermId = 'fall-2026',
}: CourseConfigurationModalProps) {
  const [selectedSections, setSelectedSections] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<'none' | 'rating' | 'difficulty' | 'name'>('none');
  const [filterOpen, setFilterOpen] = useState<boolean>(false);

useEffect(() => {
  if (course && isOpen) {
    const baseSelection = course.allowedSections && course.allowedSections.length > 0
      ? course.allowedSections
      : (course.sections ? course.sections.map(s => s.id) : []);
    const nextSelection = focusSectionId
      ? Array.from(new Set([...baseSelection, focusSectionId]))
      : baseSelection;
    setSelectedSections(nextSelection);
  }
}, [course, isOpen, focusSectionId]);

  // Auto-save when selectedSections changes
  // Function to find professor data by name
  const findProfessorData = (professorName: string): Professor | null => {
    if (!professorName || professorName === 'TBA') return null;
    
    const cleanName = professorName.replace(/,/g, '').trim().toLowerCase();
    
    return typedTeachersData.find(prof => {
      const fullName = `${prof.firstName} ${prof.lastName}`.toLowerCase();
      const reverseName = `${prof.lastName} ${prof.firstName}`.toLowerCase();
      return fullName.includes(cleanName) || reverseName.includes(cleanName) || cleanName.includes(fullName);
    }) || null;
  };

  // Helper function to get rating color
  const getRatingColor = (rating: number): string => {
    if (rating >= 4.0) return 'text-green-600 dark:text-green-400';
    if (rating >= 3.5) return 'text-green-500 dark:text-green-500';
    if (rating >= 3.0) return 'text-yellow-500 dark:text-yellow-500';
    if (rating >= 2.5) return 'text-orange-500 dark:text-orange-500';
    return 'text-red-500 dark:text-red-400';
  };

  // Get campus-based styling
  const getCampusStyle = (campus: string): string => {
    const normalizedCampus = campus?.trim() || 'Unknown';
    
    if (normalizedCampus.toLowerCase().includes('online') || 
        normalizedCampus.toLowerCase().includes('web')) {
      return CAMPUS_COLORS['Online'];
    }
    if (normalizedCampus.toLowerCase().includes('remote')) {
      return CAMPUS_COLORS['Remote'];
    }
    if (normalizedCampus.toLowerCase().includes('c/d') || 
        normalizedCampus.toLowerCase().includes('cook') ||
        normalizedCampus.toLowerCase().includes('douglass')) {
      return CAMPUS_COLORS['Cook/Douglass'];
    }
    if (normalizedCampus.toLowerCase().includes('busch')) {
      return CAMPUS_COLORS['Busch'];
    }
    if (normalizedCampus.toLowerCase().includes('college avenue') ||
        normalizedCampus.toLowerCase().includes('college ave')) {
      return CAMPUS_COLORS['College Ave'];
    }
    if (normalizedCampus.toLowerCase().includes('livingston')) {
      return CAMPUS_COLORS['Livingston'];
    }
    if (normalizedCampus === 'NB' || normalizedCampus.toLowerCase() === 'nb') {
      return CAMPUS_COLORS['NB'];
    }
    
    return CAMPUS_COLORS[normalizedCampus as keyof typeof CAMPUS_COLORS] || CAMPUS_COLORS['NB'];
  };

  // Check if a section is open
  const isSectionOpen = useCallback((sectionId: string): boolean => {
    return openSectionsService.isOpen(sectionId, openSectionsTermId);
  }, [openSectionsTermId]);

  const toggleSection = (sectionId: string) => {
    setSelectedSections(prev => 
      prev.includes(sectionId)
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const selectAll = () => {
    if (!course) return;
    setSelectedSections(course.sections.map(s => s.id));
  };

  const deselectAll = () => {
    setSelectedSections([]);
  };

  const handleSave = () => {
    onSave(selectedSections);
    onClose();
  };

  // Process and sort/filter sections
  const processedSections = useMemo(() => {
    if (!course || !course.sections) return [];
    
    let sections = [...course.sections];
    
    // Filter for open sections if enabled
    if (filterOpen) {
      sections = sections.filter(section => isSectionOpen(section.id));
    }
    
    // Sort sections
    if (sortBy !== 'none') {
      sections.sort((a, b) => {
        const instructorNamesA = a.instructors.map(instructor => 
          typeof instructor === 'string' ? instructor : instructor.name
        ).join(', ');
        const instructorNamesB = b.instructors.map(instructor => 
          typeof instructor === 'string' ? instructor : instructor.name
        ).join(', ');
        const profDataA = findProfessorData(instructorNamesA);
        const profDataB = findProfessorData(instructorNamesB);
        
        switch (sortBy) {
          case 'rating':
            return (profDataB?.avgRating || 0) - (profDataA?.avgRating || 0);
          case 'difficulty':
            return (profDataA?.avgDifficulty || 0) - (profDataB?.avgDifficulty || 0);
          case 'name':
            return a.section.localeCompare(b.section);
          default:
            return 0;
        }
      });
    }
    
    return sections;
  }, [course, filterOpen, sortBy, isSectionOpen]);

  const formatTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
    return `${displayHours}:${mins.toString().padStart(2, '0')} ${period}`;
  };

  if (!isOpen || !course) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="full"
      showCloseButton={false}
      closeOnOverlayClick={false}
      ariaLabel={`Configure Sections: ${course.name}`}
      returnFocusRef={returnFocusRef}
    >
        <div className="mx-auto max-w-4xl">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-xl font-bold text-slate-900 dark:text-white">
                Configure Sections: {course.name}
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                Select which sections you want to allow in your schedules
              </p>
            </div>
            <button
              type="button"
              aria-label="Save and close section configuration"
              onClick={handleSave}
              className="grid min-h-12 min-w-12 place-items-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Quick actions and filters */}
          <div className="flex items-center justify-between mb-4 p-3 bg-slate-50 dark:bg-slate-700 rounded-control">
            <div className="text-sm text-slate-600 dark:text-slate-400">
              {selectedSections.length} of {course.sections.length} sections selected
            </div>
            <div className="flex gap-2">
              {/* Filter and sort controls - only show if more than 5 sections */}
              {course.sections.length > 5 && (
                <>
                  <div className="flex items-center gap-2">
                    <AdjustmentsHorizontalIcon className="h-4 w-4 text-slate-500" />
                    <select
                      aria-label="Sort sections"
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value as any)}
                      className="text-sm rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white px-2 py-1"
                    >
                      <option value="none">Sort by...</option>
                      <option value="rating">Rating (high to low)</option>
                      <option value="difficulty">Difficulty (easy first)</option>
                      <option value="name">Section #</option>
                    </select>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                    <input
                      type="checkbox"
                      checked={filterOpen}
                      onChange={(e) => setFilterOpen(e.target.checked)}
                      className="rounded border-slate-300 dark:border-slate-600"
                    />
                    Open only
                  </label>
                </>
              )}
              <button
                onClick={selectAll}
                className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-control transition-colors"
              >
                Select All
              </button>
              <button
                onClick={deselectAll}
                className="px-3 py-1.5 text-sm bg-slate-600 hover:bg-slate-700 text-white rounded-control transition-colors"
              >
                Clear All
              </button>
            </div>
          </div>

          {/* Sections list */}
          <div className="space-y-2">
            {processedSections.map((section) => {
              const instructorNames = section.instructors.map(instructor => 
                typeof instructor === 'string' ? instructor : instructor.name
              ).join(', ');
              const professorData = findProfessorData(instructorNames);
              const isSelected = selectedSections.includes(section.id);
              const isOpen = isSectionOpen(section.id);
              const campusStyle = getCampusStyle(section.campuses?.[0] ?? section.campus);
              const campusDisplay = section.campuses && section.campuses.length > 0
                ? section.campuses.join(', ')
                : section.campus;
              const meetingSummary = section.meetingTimes && section.meetingTimes.length > 0
                ? section.meetingTimes.map((mt) => {
                    const dayLabel = mt.day && mt.day !== 'TBA' ? mt.day : 'TBA';
                    const startLabel = mt.startMinute ? formatTime(mt.startMinute) : 'TBA';
                    const endLabel = mt.endMinute ? formatTime(mt.endMinute) : 'TBA';
                    const locationLabel = mt.location && mt.location !== 'TBA' ? ` @ ${mt.location}` : '';
                    const campusLabel = mt.campus && mt.campus !== campusDisplay ? ` (${mt.campus})` : '';
                    return `${isRecitationMeeting(mt) ? 'Recitation · ' : ''}${dayLabel} ${startLabel}-${endLabel}${locationLabel}${campusLabel}`;
                  }).join(', ')
                : 'TBA';
              const isFocused = focusSectionId === section.id;

              return (
                <div
                  key={section.id}
                  onClick={() => toggleSection(section.id)}
                  className={`relative cursor-pointer transition-all ${
                    isSelected
                      ? 'border-2 border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                      : 'border border-slate-300 dark:border-slate-600'
                  } rounded-control p-3 hover:bg-slate-50 dark:hover:bg-slate-700 ${isFocused ? 'ring-2 ring-blue-400 dark:ring-blue-300' : ''}`}
                >
                  <div className="flex items-start gap-3">
                    {/* Selection indicator */}
                    <div className="flex-shrink-0 pt-0.5">
                      {isSelected ? (
                        <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center">
                          <CheckIcon className="h-3 w-3 text-white" />
                        </div>
                      ) : (
                        <div className="w-5 h-5 border-2 border-slate-400 rounded-full" />
                      )}
                    </div>

                    {/* Main content */}
                    <div className="flex-1 min-w-0 grid grid-cols-2 gap-2">
                      {/* Left column */}
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className={`rounded px-2 py-0.5 text-xs font-medium text-white ${campusStyle}`}>
                            {section.section}
                          </div>
                          <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                            isOpen 
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300' 
                              : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300'
                          }`}>
                            {isOpen ? 'OPEN' : 'CLOSED'}
                          </span>
                        </div>
                        <div className="text-xs font-medium text-slate-900 dark:text-white truncate">
                          {instructorNames || 'TBA'}
                          {professorData && (
                            <span className="ml-2 inline-flex items-center space-x-1">
                              <span className={`font-bold ${getRatingColor(professorData.avgRating)}`}>
                                {professorData.avgRating.toFixed(1)}
                              </span>
                              <span className="flex items-center">
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <StarSolidIcon
                                    key={star}
                                    className={`h-3 w-3 ${
                                      star <= Math.round(professorData.avgRating) 
                                        ? 'text-yellow-400' 
                                        : 'text-slate-300'
                                    }`}
                                  />
                                ))}
                              </span>
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Right column */}
                      <div className="space-y-1">
                        <div className="text-xs text-slate-900 dark:text-white font-medium mb-0.5 border-b border-slate-300 dark:border-slate-600 pb-0.5">
                          {campusDisplay} • {section.credits} cr
                        </div>
                        <div className="text-xs text-slate-600 dark:text-slate-400 font-medium mb-0.5 border-b border-slate-300 dark:border-slate-600 pb-0.5">
                          {meetingSummary}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Close button */}
          <div className="flex justify-end mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
            <button
              type="button"
              onClick={handleSave}
              className="min-h-12 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-control transition-colors"
            >
              Done
            </button>
          </div>
        </div>
    </Modal>
  );
}
